import { useState } from "react";
import { useAppSelector, usePermissions } from "@app/hooks";
import { selectCurrentProjectNote } from "@app/features/project/projectSlice";
import { useNotesApi } from "./useNotesApi"
import { useHandleProjectState } from "@pages/project/useHandleProjectState";

export const useNotes = () => {
    const notes = useAppSelector(selectCurrentProjectNote);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [lastError, setLastError] = useState<string | null>(null);
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
    const { canMutateNote, isFinalized } = usePermissions();
    const { isFetching: isProjectLoading } = useHandleProjectState();
    const { createNote, updateNote, deleteNote, isLoading, errors } = useNotesApi();
    // Clear error when starting any action
    const clearError = () => setLastError(null);
    // Update error if any of the error messages change
    const currentError = errors.update || errors.create || errors.delete || errors.moduleTypes;
    if (currentError && currentError !== lastError) {
        setLastError(currentError);
    }

    const handleSaveNotes = async (newNote: string) => {
        clearError();
        notes?.id && newNote ? await updateNote(notes.id, newNote) : await createNote(newNote);
        setEditMode(false);
    };

    const confirmDelete = (noteId: number) => {
        setPendingDeleteId(noteId);
    };

    const cancelDelete = () => {
        setPendingDeleteId(null);
    };

    const handleDeleteNote = async () => {
        if (!pendingDeleteId) return;

        clearError();
        await deleteNote(pendingDeleteId);
        setEditMode(false);
        setPendingDeleteId(null);
    };

    return {
        notes: notes ?? null,
        editMode,
        isLoading: isLoading || isProjectLoading,
        error: lastError,
        canMutateNote,
        isFinalized,
        pendingDeleteId,
        handleSaveNotes,
        setEditMode,
        confirmDelete,
        cancelDelete,
        handleDeleteNote,
    };
}; 